import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { useNavigate, Navigate, Link } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { useEnhancedToast } from "@/components/ui/enhanced-toast"
import { EyeIcon, EyeOffIcon, LockIcon, MailIcon, UserIcon, AlertCircle, ShieldX, UserCheck2, MailCheck, ShieldCheck } from 'lucide-react'
import { signup, signupMember, validateInvitation } from "@/api"
import { useAuth } from "@/context/AuthContext"
import { AuthenticationCheck } from "@/components/auth/AuthenticationCheck"
import { useTenant } from "@/context/TenantContext"
import { useTheme } from "@/context/ThemeContext"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

export default function Signup() {
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [invitationToken, setInvitationToken] = useState<string | null>(null);
    const [isEmailLocked, setIsEmailLocked] = useState(false);
    const [isValidatingInvitation, setIsValidatingInvitation] = useState(false);
    const navigate = useNavigate()
    const { success, error } = useEnhancedToast()
    const { setAuthData, isAuthenticated, isLoading: authLoading } = useAuth()
    const { branding } = useTenant();
    const { theme } = useTheme();

    // Determine if we should use dark logo
    const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const effectiveLogoUrl = isDarkMode && branding?.dark_logo_url ? branding.dark_logo_url : branding?.logo_url;

    const validateInvitationToken = async (token: string) => {
        setIsValidatingInvitation(true);
        try {
            const response = await validateInvitation({ token });
            if (response.data) {
                setEmail(response.data.email);
                setIsEmailLocked(true);
                success({
                    icon: ShieldCheck,
                    title: "Invitation Valid",
                    description: "Your invitation has been validated. Please complete the signup process.",
                    duration: 4000,
                });
            } else {
                error({
                    icon: ShieldX,
                    title: "Invalid Invitation",
                    description: "The invitation link is invalid or has expired. You can proceed with normal signup.",
                    duration: 5000,
                });
                const newUrl = window.location.pathname;
                window.history.replaceState({}, '', newUrl);
            }
        } catch (err) {
            error({
                icon: AlertCircle,
                title: "Invalid Invitation",
                description: "The invitation link is invalid or has expired. You can proceed with normal signup.",
                duration: 5000,
            });
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
        } finally {
            setIsValidatingInvitation(false);
        }
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {}
        let errorMessages: string[] = []

        if (!firstName.trim()) {
            newErrors.firstName = 'Required'
            errorMessages.push("First name is required")
        }
        if (!lastName.trim()) {
            newErrors.lastName = 'Required'
            errorMessages.push("Last name is required")
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!email.trim()) {
            newErrors.email = 'Required'
            errorMessages.push("Email address is required")
        } else if (!emailRegex.test(email)) {
            newErrors.email = 'Invalid email'
            errorMessages.push("Please enter a valid email address")
        }

        if (!password) {
            newErrors.password = 'Required'
            errorMessages.push("Password is required")
        } else {
            const passwordErrors: string[] = []
            if (password.length < 8) passwordErrors.push("at least 8 characters")
            if (!/[A-Z]/.test(password)) passwordErrors.push("one uppercase letter")
            if (!/[a-z]/.test(password)) passwordErrors.push("one lowercase letter")
            if (!/\d/.test(password)) passwordErrors.push("one number")

            if (passwordErrors.length > 0) {
                newErrors.password = 'Invalid'
                errorMessages.push(`Password must contain ${passwordErrors.join(", ")}`)
            }
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Required'
            errorMessages.push("Please confirm your password")
        } else if (confirmPassword !== password) {
            newErrors.confirmPassword = 'Mismatch'
            errorMessages.push("Passwords do not match")
        }

        return { errors: newErrors, messages: errorMessages }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        const validation = validateForm()

        if (Object.keys(validation.errors).length > 0) {
            setErrors(validation.errors)
            error({
                icon: AlertCircle,
                title: "Please fix the following:",
                description: (
                    <ul className="list-disc pl-4 mt-2 space-y-1">
                        {validation.messages.map((message, index) => (
                            <li key={index}>{message}</li>
                        ))}
                    </ul>
                ),
                duration: 5000,
            });
            return
        }

        setIsLoading(true)

        try {
            const signupData = {
                email,
                password,
                first_name: firstName,
                last_name: lastName,
            };

            let response;
            if (invitationToken) {
                response = await signupMember({
                    ...signupData,
                    token: invitationToken
                });

                if (response.data) {
                    await setAuthData();
                    success({
                        icon: UserCheck2,
                        title: "Account Created!",
                        description: "Your account has been created successfully.",
                        duration: 4000,
                    });
                } else {
                    error({
                        icon: AlertCircle,
                        title: "Sign-up Failed",
                        description: response.error || "Failed to create account",
                        duration: 4000,
                    });
                }
            } else {
                response = await signup(signupData);

                if (response.data) {
                    success({
                        icon: MailCheck,
                        title: "Account Created!",
                        description: response.data?.message || "Please check your email to verify your account.",
                        duration: 5000,
                    });
                    navigate('/auth/login', { replace: true });
                } else {
                    error({
                        icon: AlertCircle,
                        title: "Sign-up Failed",
                        description: "Unable to complete sign-up. Please check your details or try again later.",
                        duration: 4000,
                    });
                }
            }
        } catch (error) {
            error({
                icon: AlertCircle,
                title: "Error",
                description: "An unexpected error occurred",
                duration: 4000,
            })
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        if (token) {
            setInvitationToken(token);
            validateInvitationToken(token);
        }
    }, []);

    if (authLoading) {
        return <AuthenticationCheck />;
    }

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    const containerVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        }
    }

    const renderLogo = () => {
        if (!effectiveLogoUrl) {
            return (
                <span className="text-2xl font-semibold text-primary">
                    {branding?.company_name || 'VoiceBot'}
                </span>
            );
        }

        return (
            <img
                src={effectiveLogoUrl}
                alt={branding?.company_name || 'Company Logo'}
                className="max-h-12 sm:max-h-14 w-auto max-w-[180px] sm:max-w-[220px] object-contain"
                onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    const parent = target.parentElement;
                    if (parent) {
                        parent.innerHTML = `<span class="text-2xl font-semibold text-primary">${branding?.company_name || 'VoiceBot'}</span>`;
                    }
                }}
            />
        );
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="w-full max-w-md mx-auto"
        >
            <Card className="shadow-lg border relative">
                {isValidatingInvitation && (
                    <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-lg">
                        <div className="flex items-center gap-3">
                            <div className="w-4 h-4 border-2 border-primary/80 border-t-transparent rounded-full animate-spin" />
                            <p className="text-sm text-foreground">Validating invitation...</p>
                        </div>
                    </div>
                )}
                <CardContent className="p-6 sm:p-8 space-y-6">
                    {/* Logo */}
                    <div className="flex flex-col items-center space-y-6">
                        <div className="flex items-center justify-center min-h-12 max-h-16 py-2">
                            {renderLogo()}
                        </div>
                        <div className="text-center space-y-1.5">
                            <h1 className="text-xl font-semibold tracking-tight">Create your account</h1>
                            <p className="text-sm text-muted-foreground/80">
                                Get started with your free account
                            </p>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-sm font-medium">
                                Email
                            </Label>
                            <div className="relative">
                                <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isEmailLocked}
                                    className={cn("pl-9 h-9", errors.email && 'border-destructive')}
                                    required
                                />
                            </div>
                        </div>

                        {/* First Name and Last Name */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="firstName" className="text-sm font-medium">
                                    First Name
                                </Label>
                                <div className="relative">
                                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="firstName"
                                        type="text"
                                        placeholder="First name"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        className={cn("pl-9 h-9", errors.firstName && 'border-destructive')}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="lastName" className="text-sm font-medium">
                                    Last Name
                                </Label>
                                <div className="relative">
                                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="lastName"
                                        type="text"
                                        placeholder="Last name"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        className={cn("pl-9 h-9", errors.lastName && 'border-destructive')}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Password */}
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-sm font-medium">
                                Password
                            </Label>
                            <div className="relative">
                                <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className={cn("pl-9 pr-9 h-9", errors.password && 'border-destructive')}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showPassword ? (
                                        <EyeOffIcon className="h-4 w-4" />
                                    ) : (
                                        <EyeIcon className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="text-sm font-medium">
                                Confirm Password
                            </Label>
                            <div className="relative">
                                <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirm your password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className={cn("pl-9 pr-9 h-9", errors.confirmPassword && 'border-destructive')}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showConfirmPassword ? (
                                        <EyeOffIcon className="h-4 w-4" />
                                    ) : (
                                        <EyeIcon className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className={cn("w-full", !isEmailLocked && "cursor-not-allowed")}>
                                        <Button
                                            type="submit"
                                            className="w-full h-9"
                                            disabled={!isEmailLocked || isLoading}
                                        >
                                            {isLoading ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Creating Account...
                                                </span>
                                            ) : (
                                                'Create Account'
                                            )}
                                        </Button>
                                    </div>
                                </TooltipTrigger>
                                {!isEmailLocked && (
                                    <TooltipContent side="top" className="max-w-[300px]">
                                        <div className="space-y-1">
                                            <p className="font-semibold text-sm">Registration Closed</p>
                                            <p className="text-xs text-muted-foreground">
                                                This is a closed system. Please contact support to request access.
                                            </p>
                                        </div>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    </form>

                    {/* Login Link */}
                    <div className="text-center text-sm text-muted-foreground/80">
                        Already have an account?{" "}
                        <button
                            onClick={() => navigate("/")}
                            className="text-primary hover:text-primary/80 transition-colors font-medium"
                        >
                            Sign in
                        </button>
                    </div>
                </CardContent>
            </Card>

            {/* Footer */}
            <div className="mt-6 text-center space-y-2">
                <p className="text-xs text-muted-foreground/70">
                    By signing up, you agree to our
                </p>
                <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground/70">
                    <Link 
                        to="/terms-of-service" 
                        className="hover:text-foreground/80 transition-colors"
                    >
                        Terms of Service
                    </Link>
                    <span>•</span>
                    <Link 
                        to="/privacy-policy" 
                        className="hover:text-foreground/80 transition-colors"
                    >
                        Privacy Policy
                    </Link>
                </div>
            </div>
        </motion.div>
    )
}
